/**
 * Pantalla para quien entra sin municipio y no es staff (issue #624).
 *
 * Por R-SEC-003 solo el staff puede crear un tenant o unirse a uno ajeno:
 * para cualquier otra cuenta, elegir municipio en el wizard terminaba
 * siempre en un 403, y el wizard no tenía salida (sin "Cerrar sesión" desde
 * el Sprint 10 · 5). Es la pantalla "Tu cuenta requiere activación" que ya
 * describía el flujo de aprovisionamiento (`admin/provision.py`): dice qué
 * pasa, qué hacer y ofrece salir.
 *
 * Mismo esqueleto que `TenantPausado`: pantalla completa, tarjeta y pie de
 * salida fuera de la tarjeta.
 */
import { useEffect, useState } from "react";

import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import { useMunicipality } from "@/lib/municipality";

const SOPORTE = "soporte@kaptar.com.co";

export function CuentaSinVincular() {
  const { user, signOut } = useAuth();
  const { refetch } = useMunicipality();
  const [revisando, setRevisando] = useState(false);
  const [sinNovedad, setSinNovedad] = useState(false);

  // Cuántas cuentas llegan acá sin invitación: una por montaje.
  useEffect(() => {
    track({ event_name: "onboarding_cuenta_sin_vincular_viewed" });
  }, []);

  async function revisarDeNuevo() {
    setRevisando(true);
    setSinNovedad(false);
    try {
      await refetch();
    } finally {
      setRevisando(false);
    }
    // Si ya lo invitaron, el contexto trae el municipio y el wizard sigue
    // solo; si esta pantalla sigue montada, es que no hay novedad.
    setSinNovedad(true);
  }

  return (
    <div
      data-testid="cuenta-sin-vincular"
      className="flex min-h-screen items-center justify-center bg-surface px-4 py-10"
    >
      <div className="w-full max-w-md">
        <header className="mb-6 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600">
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
              <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
              <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <p className="text-lg font-extrabold tracking-tight text-slate-900">Kaptar</p>
        </header>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs sm:p-8">
          <h1 className="text-xl font-extrabold tracking-tight text-slate-900">
            Tu cuenta todavía no está vinculada a una alcaldía
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {user?.email ? (
              <>
                Entraste como <strong className="text-slate-900">{user.email}</strong>, pero ninguna
                alcaldía te ha invitado a Kaptar todavía.
              </>
            ) : (
              "Ninguna alcaldía te ha invitado a Kaptar todavía."
            )}
          </p>

          <ul className="mt-4 space-y-3 text-sm text-slate-700">
            <li>
              <strong className="text-slate-900">Si tu alcaldía ya usa Kaptar,</strong> pídele a
              quien lo administra que te invite desde Membresía. Te llegará un correo.
            </li>
            <li>
              <strong className="text-slate-900">Si tu alcaldía todavía no lo usa,</strong>{" "}
              escríbenos a{" "}
              <a
                href={`mailto:${SOPORTE}`}
                className="font-semibold text-brand-700 underline hover:text-brand-800"
              >
                {SOPORTE}
              </a>{" "}
              y te ayudamos a activarla.
            </li>
            <li>
              <strong className="text-slate-900">¿Entraste con otra cuenta?</strong> Cierra sesión y
              entra con el correo al que te invitaron.
            </li>
          </ul>

          <button
            type="button"
            onClick={() => void revisarDeNuevo()}
            disabled={revisando}
            className="mt-6 w-full rounded-md bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-wait disabled:opacity-60"
          >
            {revisando ? "Revisando…" : "Revisar de nuevo"}
          </button>
          {sinNovedad && (
            <p role="status" className="mt-2 text-center text-xs text-slate-500">
              Todavía no hay una invitación para esta cuenta.
            </p>
          )}
        </div>

        <nav
          aria-label="Salir de la cuenta"
          className="mt-4 flex items-center justify-center text-xs text-slate-500"
        >
          <button
            type="button"
            onClick={() => {
              void signOut();
            }}
            className="inline-flex min-h-11 items-center px-2 font-semibold text-slate-600 hover:underline"
          >
            Cerrar sesión
          </button>
        </nav>
      </div>
    </div>
  );
}
